/*
 * @providesModule SheenEffect
 */
import React, {useEffect, useRef, useState, useMemo} from 'react';
import {Animated, LayoutChangeEvent, View, ViewStyle} from 'react-native';
import styles from './SheenEffect.style';
import Sizes from 'Sizes';

const SheenEffect = ({
  children,
  showEffect = false,
  style,
  containerStyle,
  loopDuration = Sizes.select({phone: 2000, tablet: 2500}),
}: SheenEffectProps) => {
  if (!showEffect) { 
    return (<React.Fragment>{children}</React.Fragment>); 
  }

  const highlightProgress = useRef(new Animated.Value(0)).current;
  const [highlightLoop, setHighlightLoop] = useState<Animated.CompositeAnimation>(Animated.loop(
    Animated.timing(highlightProgress, {
      toValue: 120,
      useNativeDriver: true,
      duration: loopDuration,
    }),
  ));
  
  const [effectHeight, setEffectHeight] = useState<number>(0);
  const [effectWidth, setEffectWidth] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const [cellWidth, setCellWidth] = useState<number>(0);
  const [highlightPosition, setHighlightPosition] = useState<HighlightPosition>({height: 0, top: 0});
  
  const HIGHLIGHT_1_WIDTH = 10;
  const HIGHLIGHT_2_WIDTH = 25;
  const rotationAngle = 35;

  const HIGHLIGHT_1_INTERPOLATION = {
    inputRange: [0, 35, 36, 64, 65, 120],
    outputRange: [-(offset + HIGHLIGHT_1_WIDTH), -(offset + HIGHLIGHT_1_WIDTH), -(offset + HIGHLIGHT_1_WIDTH), cellWidth + offset, cellWidth + offset, cellWidth + offset],
  };
  const HIGHLIGHT_2_INTERPOLATION = {
    inputRange: [0, 19, 20, 59, 60, 120],
    outputRange: [-(offset + HIGHLIGHT_2_WIDTH), -(offset + HIGHLIGHT_2_WIDTH), -(offset + HIGHLIGHT_2_WIDTH), cellWidth + offset, cellWidth + offset, cellWidth + offset],
  };
  
  const newX1 = useMemo(() => highlightProgress.interpolate(HIGHLIGHT_1_INTERPOLATION), [HIGHLIGHT_1_INTERPOLATION]);
  const newX2 = useMemo(() => highlightProgress.interpolate(HIGHLIGHT_2_INTERPOLATION), [HIGHLIGHT_2_INTERPOLATION]);

  const radiansToDegrees = useMemo(() => rotationAngle * 180 / Math.PI, [rotationAngle]);

  useEffect(() => {
    if (showEffect) {
      highlightLoop.start();
    }
  }, []);

  useEffect(() => {
    setCellWidth(effectWidth);
    setOffset(((effectHeight / Math.sin(radiansToDegrees) + 20) / 2) * Math.cos(radiansToDegrees));
  }, [effectWidth]);

  useEffect(() => {
    setHighlightPosition({
      // Calculates the Highlight's height in order to cover the whole cell when a rotation is applied.
      // We add extra value to make sure we cover the corners.
      height: (effectHeight / Math.sin(radiansToDegrees) + 20),
      // Rotation makes the highlight going down on the cell based on its center point
      // We add some negative marging in order to realign the center of the highlight.
      top: Sizes.select({phone: -16, tablet: -24}),
    });
  }, [effectHeight]);

  return (
    <View onLayout={(e: LayoutChangeEvent) => {
      setEffectHeight(e.nativeEvent.layout.height);
      setEffectWidth(e.nativeEvent.layout.width);
      }}
      style={[style, {overflow: 'hidden'}, containerStyle]}
    >
      {children}
      <Animated.View style={[styles.highlight, highlightPosition, {width: HIGHLIGHT_1_WIDTH, transform: [{translateX: newX1}, {rotate: `${rotationAngle}deg`}]}]} />
      <Animated.View style={[styles.highlight, highlightPosition, {width: HIGHLIGHT_2_WIDTH, transform: [{translateX: newX2}, {rotate: `${rotationAngle}deg`}]}]} />
    </View>
  );
};

interface SheenEffectProps {
  children?: React.ReactNode;
  showEffect: Boolean;
  style?: ViewStyle;
  containerStyle?: ViewStyle;
  rotationAngle?: number;
  loopDuration: number;
}

interface HighlightPosition {
  height: number,
  top: number,
}

export default SheenEffect;
